import { Component, inject, Input, signal, computed } from "@angular/core";
import {
    CompanyEntity,
    PointEntity,
    VisualizationEntity,
    SerieEntity
} from "@arch/domain/entity";
import { DateUtils, FormatNumbers } from "@arch/_shared/utils";
import { ColorsService } from "@arch/domain/application/services/colors.service";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { GetVisualizationDataController } from "@arch/presentation/controllers";
import { PeriodNames } from "@arch/_shared/types/interval-dates";
@Component({
    selector: "app-report-table-values-pdf",
    imports: [SharedMaterialModule],
    templateUrl: "./report-table-values-pdf.component.html",
    styleUrl: "./report-table-values-pdf.component.scss"
})
export class ReportTableValuesPdfComponent {
    @Input() set options(value: VisualizationOptionsType) {
        this.optionsSignal.set(value);
    }
    get options() {
        return this.optionsSignal();
    }
    private readonly optionsSignal = signal<VisualizationOptionsType>(null);

    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: true }) visualization: VisualizationEntity;
    @Input({ required: true }) columnDates: Date[];

    private readonly getVisualizationDataController = inject(
        GetVisualizationDataController
    );

    private getData(): {
        series: SerieEntity[];
        points: PointEntity[];
    } {
        const { series, points } = this.getVisualizationDataController.execute({
            company: this.company,
            options: this.options,
            visualization: this.visualization
        });
        return { series, points };
    }

    public readonly tableDates = computed(
        (): {
            formatted: string;
            date: Date;
            scenario?: string;
        }[] => {
            if (!this.options) return [];
            const columnDates: {
                formatted: string;
                date: Date;
                scenario?: string;
            }[] = [];
            for (const date of this.columnDates) {
                columnDates.push({
                    formatted: DateUtils.formatByPeriodName(
                        this.options.period,
                        date,
                        true
                    ),
                    date,
                    scenario: this.getScenarioName(date)
                });
                if (this.options.budget) {
                    columnDates.push({
                        formatted: DateUtils.formatByPeriodName(
                            this.options.period,
                            date,
                            true
                        ),
                        date,
                        scenario: "Orçamento"
                    });
                }
            }
            return columnDates;
        }
    );

    public readonly tableRows = computed((): TableRowsType[] => {
        if (!this.options) return [];
        const { series, points } = this.getData();
        return this.getTableRows(series, points);
    });

    private getTableRows(
        series: SerieEntity[],
        points: PointEntity[]
    ): TableRowsType[] {
        const rows = this.getTableRowsDefault(series, points);
        return rows;
    }

    private getTableRowsDefault(
        series: SerieEntity[],
        points: PointEntity[]
    ): TableRowsType[] {
        const tableRows: TableRowsType[] = [];
        series.forEach((serie) => {
            const serieOptions = this.visualization.props.chart?.series.find(
                (s: any) => s._id === serie.props._id
            );
            const row: TableRowsType = {
                serie,
                serieOptions,
                color: ColorsService.getColor(
                    serieOptions?.color || "petroleum-medium"
                ),
                results: [] as ResultsType[]
            };
            for (const date of this.columnDates) {
                const value = this.getPointValue(points, {
                    date,
                    serie,
                    scenarioId: this.options.scenario.props._id,
                    period: this.options.period
                });
                row.results.push({
                    label: "Resultado",
                    value: value
                });
                if (this.options.budget) {
                    const valueBudget = this.getPointValue(points, {
                        date,
                        serie,
                        scenarioId: this.options.budget,
                        period: this.options.period
                    });
                    row.results.push({
                        label: "Orçamento",
                        value: valueBudget
                    });
                }
            }
            tableRows.push(row);
        });
        return tableRows;
    }

    private getPointValue(
        points: PointEntity[],
        options: {
            date: Date;
            serie: SerieEntity;
            scenarioId: string;
            period: PeriodNames;
        }
    ): string {
        const point = points.find(
            (p) =>
                p.t.getTime() === options.date.getTime() &&
                p.props.s === options.serie.props._id &&
                p.props.sc === options.scenarioId &&
                p.props.period === options.period
        );
        const valueFormatted = FormatNumbers.byUnit(
            options.serie.props.unit,
            point ? point.props.m.v : 0,
            2
        );
        return valueFormatted;
    }

    private getScenarioName(date: Date): string {
        if (this.options.budget) {
            return "Base";
        }
        if (!this.options.comparation.active) return "";
        if (this.options.comparation.allMonths) {
            return date.getUTCFullYear() ===
                this.options.comparation.date.getUTCFullYear()
                ? "Comparado"
                : "Base";
        }
        return +date === +this.options.comparation.date ? "Comparado" : "Base";
    }
}

type TableRowsType = {
    serie: SerieEntity;
    results: ResultsType[];
    serieOptions: any;
    color: string;
};

type ResultsType = {
    label: string;
    value: string;
    isComparation?: boolean;
    isBudget?: boolean;
    isProjection?: boolean;
};
